import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ReactFlow, Background, Controls, MiniMap,
  addEdge, useNodesState, useEdgesState, Handle, Position,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { Plus, Save, RotateCcw } from 'lucide-react';
import { api } from '../api';
import { PageHeader, Card, Button, Field, Input, Select, fmtR } from '../components/ui';

/* ─── evaluation engine ──────────────────────────────────────────────── */

function applyOp(op, a, b) {
  switch (op) {
    case '+': return a + b;
    case '-': return a - b;
    case '*': return a * b;
    case '/': return b === 0 ? NaN : a / b;
    default:  return NaN;
  }
}

function evaluate(nodes, edges) {
  const byId = Object.fromEntries(nodes.map((n) => [n.id, n]));
  const incoming = {};
  edges.forEach((e) => {
    incoming[e.target] = incoming[e.target] || {};
    incoming[e.target][e.targetHandle || 'in'] = e.source;
  });
  const memo = {};
  const stack = new Set();
  function val(id) {
    if (id in memo) return memo[id];
    if (stack.has(id)) return NaN; // cycle guard
    stack.add(id);
    const n = byId[id];
    let v = NaN;
    if (n) {
      if (n.type === 'input' || n.type === 'const') {
        v = Number(n.data.value) || 0;
      } else if (n.type === 'op') {
        const inc = incoming[id] || {};
        const a = inc.a != null ? val(inc.a) : 0;
        const b = inc.b != null ? val(inc.b) : 0;
        v = applyOp(n.data.op, a, b);
      } else if (n.type === 'output') {
        const inc = incoming[id] || {};
        v = inc.in != null ? val(inc.in) : NaN;
      }
    }
    stack.delete(id);
    memo[id] = v;
    return v;
  }
  nodes.forEach((n) => val(n.id));
  return memo;
}

/* ─── custom nodes ───────────────────────────────────────────────────── */

const handleStyle = { width: 9, height: 9, background: '#e92030', border: 'none' };

function InputNode({ id, data }) {
  return (
    <div className="bg-slate-800 border border-slate-600 rounded-lg px-3 py-2 min-w-[150px] shadow">
      <div className="text-[10px] uppercase tracking-wide text-slate-500">{data.label}</div>
      <input
        type="number"
        value={data.value}
        onChange={(e) => data.onChange(id, e.target.value)}
        className="bg-transparent text-slate-100 text-sm font-semibold w-full focus:outline-none nums"
      />
      <Handle type="source" position={Position.Right} style={handleStyle} />
    </div>
  );
}

function ConstNode({ id, data }) {
  return (
    <div className="bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 min-w-[110px] shadow">
      <div className="text-[10px] uppercase tracking-wide text-slate-500">constant</div>
      <input
        type="number"
        value={data.value}
        onChange={(e) => data.onChange(id, e.target.value)}
        className="bg-transparent text-slate-100 text-sm font-semibold w-full focus:outline-none nums"
      />
      <Handle type="source" position={Position.Right} style={handleStyle} />
    </div>
  );
}

function OpNode({ id, data }) {
  return (
    <div className="bg-slate-800 border border-slate-600 rounded-lg px-2 py-3 shadow relative min-w-[90px]">
      <Handle type="target" position={Position.Left} id="a" style={{ ...handleStyle, top: 14 }} />
      <Handle type="target" position={Position.Left} id="b" style={{ ...handleStyle, top: 36 }} />
      <div className="flex flex-col items-center gap-1">
        <select
          value={data.op}
          onChange={(e) => data.onOp(id, e.target.value)}
          className="bg-slate-900 border border-slate-700 rounded text-slate-100 text-lg font-bold text-center w-12"
        >
          {['+', '-', '*', '/'].map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
        <div className="text-[11px] text-slate-400 nums">{Number.isFinite(data._value) ? fmtR(data._value) : '—'}</div>
      </div>
      <Handle type="source" position={Position.Right} style={handleStyle} />
    </div>
  );
}

function OutputNode({ id, data }) {
  return (
    <div className="bg-brand-500/10 border-2 border-brand-500 rounded-xl px-4 py-3 min-w-[190px] shadow">
      <Handle type="target" position={Position.Left} id="in" style={handleStyle} />
      <div className="flex items-center gap-1.5 mb-0.5">
        <span className="text-[10px] uppercase tracking-wide text-brand-500">output</span>
        <select
          value={data.output || 'net_profit'}
          onChange={(e) => data.onOutput?.(id, e.target.value)}
          className="bg-slate-900 border border-slate-700 rounded text-[10px] text-slate-200 px-1"
        >
          <option value="net_profit">net_profit</option>
          <option value="gross_profit">gross_profit</option>
        </select>
      </div>
      <div className="text-xl font-bold text-slate-100 nums">
        {Number.isFinite(data._value) ? fmtR(data._value) : '—'}
      </div>
      {data.compare != null && Number.isFinite(data._value) && (
        <div className="text-[11px] text-slate-400 mt-1">
          committed {fmtR(data.compare)} · Δ{' '}
          <span className={data._value - data.compare >= 0 ? 'text-emerald-600' : 'text-brand-500'}>
            {fmtR(data._value - data.compare)}
          </span>
        </div>
      )}
    </div>
  );
}

const nodeTypes = { input: InputNode, const: ConstNode, op: OpNode, output: OutputNode };

/* ─── default profitability graph ────────────────────────────────────── */

function defaultGraph() {
  return {
    nodes: [
      { id: 'revenue',  type: 'input', position: { x: 0,   y: 0   }, data: { label: 'Revenue', value: 0, field: 'revenue' } },
      { id: 'labour',   type: 'input', position: { x: 0,   y: 90  }, data: { label: 'Labour cost', value: 0, field: 'labour_cost' } },
      { id: 'overhead', type: 'input', position: { x: 0,   y: 180 }, data: { label: 'Overhead', value: 0, field: 'overhead_allocated' } },
      { id: 'gross',    type: 'op',    position: { x: 230, y: 30  }, data: { op: '-' } },
      { id: 'net',      type: 'op',    position: { x: 430, y: 90  }, data: { op: '-' } },
      { id: 'out',      type: 'output', position: { x: 640, y: 95 }, data: { label: 'Net profit', output: 'net_profit' } },
    ],
    edges: [
      { id: 'e1', source: 'revenue',  target: 'gross', targetHandle: 'a' },
      { id: 'e2', source: 'labour',   target: 'gross', targetHandle: 'b' },
      { id: 'e3', source: 'gross',    target: 'net',   targetHandle: 'a' },
      { id: 'e4', source: 'overhead', target: 'net',   targetHandle: 'b' },
      { id: 'e5', source: 'net',      target: 'out',   targetHandle: 'in' },
    ],
  };
}

let _id = 100;
const nextId = () => `n${_id++}`;

/* ─── page ───────────────────────────────────────────────────────────── */

export default function Sandbox() {
  const init = useMemo(defaultGraph, []);
  const [nodes, setNodes, onNodesChange] = useNodesState(init.nodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(init.edges);

  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState('');
  const [loaded, setLoaded] = useState(null);   // {inputs, committed, project_name, month}
  const [scenarios, setScenarios] = useState([]);
  const [scenarioName, setScenarioName] = useState('');
  const [active, setActive] = useState(null);
  const [promoteLabel, setPromoteLabel] = useState('');
  const [msg, setMsg] = useState(null);

  const loadActive = () => api.sandbox.activeFormula().then((d) => setActive(d?.formula_id ? d : null)).catch(() => {});
  useEffect(() => {
    api.sandbox.projects().then(setProjects).catch(() => {});
    api.sandbox.scenarios().then(setScenarios).catch(() => {});
    loadActive();
  }, []);

  // graph stripped of render-only fields, ready to persist
  const cleanGraph = () => ({
    nodes: nodes.map(({ id, type, position, data }) => ({
      id, type, position,
      data: Object.fromEntries(
        Object.entries(data).filter(([k]) =>
          !k.startsWith('_') && !['onChange', 'onOp', 'onOutput', 'compare'].includes(k))
      ),
    })),
    edges,
  });

  // node value editing
  const onValueChange = useCallback((id, value) => {
    setNodes((ns) => ns.map((n) => (n.id === id ? { ...n, data: { ...n.data, value } } : n)));
  }, [setNodes]);
  const onOpChange = useCallback((id, op) => {
    setNodes((ns) => ns.map((n) => (n.id === id ? { ...n, data: { ...n.data, op } } : n)));
  }, [setNodes]);
  const onOutputChange = useCallback((id, output) => {
    setNodes((ns) => ns.map((n) => (n.id === id ? { ...n, data: { ...n.data, output } } : n)));
  }, [setNodes]);

  const onConnect = useCallback((c) => setEdges((es) => addEdge(c, es)), [setEdges]);

  // live evaluation
  const values = useMemo(() => evaluate(nodes, edges), [nodes, edges]);

  // inject handlers + computed values + compare into node data for render
  const renderNodes = useMemo(() => nodes.map((n) => ({
    ...n,
    data: {
      ...n.data,
      onChange: onValueChange,
      onOp: onOpChange,
      onOutput: onOutputChange,
      _value: values[n.id],
      compare: n.type === 'output' ? (loaded?.committed?.net_profit ?? null) : undefined,
    },
  })), [nodes, values, onValueChange, onOpChange, onOutputChange, loaded]);

  const loadProject = async (pid) => {
    setProjectId(pid);
    if (!pid) return;
    try {
      const d = await api.sandbox.inputs(pid);
      setLoaded(d);
      // seed input nodes whose data.field matches
      setNodes((ns) => ns.map((n) => {
        if (n.type === 'input' && n.data.field && d.inputs[n.data.field] != null) {
          return { ...n, data: { ...n.data, value: d.inputs[n.data.field] } };
        }
        return n;
      }));
      setMsg(null);
    } catch (e) {
      setMsg({ kind: 'err', text: e.message });
    }
  };

  const addNode = (type) => {
    const id = nextId();
    const base = { id, position: { x: 250 + Math.random() * 120, y: 280 + Math.random() * 80 } };
    if (type === 'op')    setNodes((ns) => [...ns, { ...base, type: 'op', data: { op: '+' } }]);
    if (type === 'const') setNodes((ns) => [...ns, { ...base, type: 'const', data: { value: 0 } }]);
    if (type === 'input') setNodes((ns) => [...ns, { ...base, type: 'input', data: { label: 'Custom', value: 0 } }]);
  };

  const reset = () => {
    const g = defaultGraph();
    setNodes(g.nodes); setEdges(g.edges); setLoaded(null); setProjectId('');
  };

  const save = async () => {
    if (!scenarioName.trim()) { setMsg({ kind: 'err', text: 'Name the scenario first.' }); return; }
    try {
      await api.sandbox.saveScenario(scenarioName, cleanGraph(), null);
      setScenarioName('');
      api.sandbox.scenarios().then(setScenarios);
      setMsg({ kind: 'ok', text: 'Scenario saved.' });
    } catch (e) {
      setMsg({ kind: 'err', text: e.message });
    }
  };

  const promote = async () => {
    if (!promoteLabel.trim()) { setMsg({ kind: 'err', text: 'Label the formula first.' }); return; }
    try {
      const graph = cleanGraph();
      const v = await api.sandbox.validate(promoteLabel, graph);
      if (!v.valid) { setMsg({ kind: 'err', text: `Invalid: ${v.problems.join('; ')}` }); return; }
      await api.sandbox.promote(promoteLabel, graph, null);
      setPromoteLabel('');
      loadActive();
      setMsg({ kind: 'ok', text: 'Promoted. Active from the next recalculation.' });
    } catch (e) {
      setMsg({ kind: 'err', text: e.message });
    }
  };

  const loadScenario = async (id) => {
    if (!id) return;
    const s = await api.sandbox.scenario(id);
    const g = s.graph_json;
    if (g?.nodes) setNodes(g.nodes);
    if (g?.edges) setEdges(g.edges);
    setMsg({ kind: 'ok', text: `Loaded "${s.name}".` });
  };

  const netValue = values['out'];
  const committed = loaded?.committed?.net_profit;

  return (
    <div>
      <PageHeader
        eyebrow="What-if"
        title="Profitability sandbox"
        subtitle="Wire the formula yourself. Load a real project's numbers, rearrange the graph, and see net profit recompute live. Nothing here is written back to the warehouse."
      />

      <div className="grid grid-cols-1 xl:grid-cols-[1fr_320px] gap-6">
        {/* canvas */}
        <Card padding="p-0" className="overflow-hidden">
          <div style={{ height: 560 }} className="bg-slate-900">
            <ReactFlow
              nodes={renderNodes}
              edges={edges}
              onNodesChange={onNodesChange}
              onEdgesChange={onEdgesChange}
              onConnect={onConnect}
              nodeTypes={nodeTypes}
              fitView
              proOptions={{ hideAttribution: true }}
            >
              <Background color="#2a2a30" gap={18} />
              <Controls showInteractive={false} />
              <MiniMap pannable zoomable nodeColor="#e92030" maskColor="rgba(0,0,0,0.4)" />
            </ReactFlow>
          </div>
        </Card>

        {/* side panel */}
        <div className="space-y-6">
          <Card title="Load real data">
            <Field label="Project">
              <Select value={projectId} onChange={(e) => loadProject(e.target.value)}>
                <option value="">— pick a project —</option>
                {projects.map((p) => (
                  <option key={p.project_id} value={p.project_id}>{p.project_name}</option>
                ))}
              </Select>
            </Field>
            {loaded && (
              <p className="text-xs text-slate-500 mt-3">
                {loaded.project_name} · {loaded.month}
                {loaded.committed?.cost_is_estimated && <> · <span className="text-amber-600">est cost</span></>}
              </p>
            )}
          </Card>

          <Card title="Result">
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-slate-500">Graph net profit</span>
                <span className="font-semibold text-slate-100 nums">
                  {Number.isFinite(netValue) ? fmtR(netValue) : '—'}
                </span>
              </div>
              {committed != null && (
                <>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Committed</span>
                    <span className="text-slate-300 nums">{fmtR(committed)}</span>
                  </div>
                  <div className="flex justify-between border-t border-slate-700 pt-2">
                    <span className="text-slate-500">Delta</span>
                    <span className={`font-semibold nums ${Number.isFinite(netValue) && netValue - committed >= 0 ? 'text-emerald-600' : 'text-brand-500'}`}>
                      {Number.isFinite(netValue) ? fmtR(netValue - committed) : '—'}
                    </span>
                  </div>
                </>
              )}
            </div>
          </Card>

          <Card title="Build">
            <div className="flex flex-wrap gap-2">
              <Button size="sm" variant="secondary" onClick={() => addNode('op')}><Plus className="w-3.5 h-3.5" /> Op</Button>
              <Button size="sm" variant="secondary" onClick={() => addNode('const')}><Plus className="w-3.5 h-3.5" /> Const</Button>
              <Button size="sm" variant="secondary" onClick={() => addNode('input')}><Plus className="w-3.5 h-3.5" /> Input</Button>
              <Button size="sm" variant="ghost" onClick={reset}><RotateCcw className="w-3.5 h-3.5" /> Reset</Button>
            </div>
            <p className="text-[11px] text-slate-500 mt-3">Drag from a node's red handle to another to wire. Op nodes take two inputs (a, b).</p>
          </Card>

          <Card title="Active formula">
            {active ? (
              <p className="text-xs text-slate-400 mb-3">
                <span className="text-slate-100 font-medium">{active.label}</span><br />
                promoted by {active.promoted_by} · {new Date(active.promoted_at).toISOString().slice(0, 10)}
              </p>
            ) : (
              <p className="text-xs text-slate-500 mb-3">No formula promoted — engine uses the default arithmetic.</p>
            )}
            <div className="flex gap-2">
              <Input value={promoteLabel} onChange={(e) => setPromoteLabel(e.target.value)} placeholder="Formula label" className="flex-1" />
              <Button size="sm" onClick={promote}>Promote</Button>
            </div>
            <p className="text-[11px] text-slate-500 mt-2">
              Promotes the current graph as the live calc. Validated first (must produce a finite net_profit). Takes effect next recalculation.
            </p>
          </Card>

          <Card title="Scenarios">
            <div className="flex gap-2">
              <Input value={scenarioName} onChange={(e) => setScenarioName(e.target.value)} placeholder="Scenario name" className="flex-1" />
              <Button size="sm" onClick={save}><Save className="w-3.5 h-3.5" /> Save</Button>
            </div>
            <Field label="Load saved" className="mt-3">
              <Select defaultValue="" onChange={(e) => loadScenario(e.target.value)}>
                <option value="">— saved scenarios —</option>
                {scenarios.map((s) => (
                  <option key={s.scenario_id} value={s.scenario_id}>{s.name}</option>
                ))}
              </Select>
            </Field>
            {msg && (
              <p className={`text-xs mt-3 ${msg.kind === 'ok' ? 'text-emerald-600' : 'text-brand-500'}`}>{msg.text}</p>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
}
